import React, { useState, useCallback } from 'react';
import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import './ProductGallery.css';

const ProductGallery = ({
  images = [],
  generatedProductPhoto,
  isLoading = false,
  className = '',
  coverImageIndex = 0
}) => {
  // Sort images so cover is first
  const sortedImages = [...(images || [])];
  if (coverImageIndex > 0 && coverImageIndex < sortedImages.length) {
    const coverImage = sortedImages.splice(coverImageIndex, 1)[0];
    sortedImages.unshift(coverImage);
  }

  // If there's a generated product photo, prepend it as the cover
  if (generatedProductPhoto?.url) {
    sortedImages.unshift(generatedProductPhoto);
  }

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const selectedImage = sortedImages[selectedIndex];

  const handlePrev = useCallback(() => {
    setSelectedIndex(prev => (prev === 0 ? sortedImages.length - 1 : prev - 1));
  }, [sortedImages.length]);

  const handleNext = useCallback(() => {
    setSelectedIndex(prev => (prev === sortedImages.length - 1 ? 0 : prev + 1));
  }, [sortedImages.length]);

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="aspect-w-1 aspect-h-1 w-full">
          <div className="w-full h-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="mt-4 flex gap-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="w-20 h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!sortedImages?.length) {
    return (
      <div className={`${className} aspect-w-1 aspect-h-1 w-full bg-gray-100 rounded-lg flex items-center justify-center`}>
        <span className="text-gray-400">No images available</span>
      </div>
    );
  }

  return (
    <div className={`${className} product-gallery-custom`}>
      {/* Main image */}
      <div className="product-gallery-main">
        <img
          src={selectedImage?.url}
          alt={`Product image ${selectedIndex + 1}`}
          className="product-gallery-main-img"
          draggable={false}
        />

        {/* Nav arrows (only show if multiple images) */}
        {sortedImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="product-gallery-nav product-gallery-nav-left"
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              onClick={handleNext}
              className="product-gallery-nav product-gallery-nav-right"
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}

        {/* Fullscreen button */}
        <button
          onClick={() => setIsFullscreen(true)}
          className="product-gallery-fullscreen-btn"
          aria-label="View fullscreen"
        >
          <ArrowsPointingOutIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Thumbnail strip */}
      {sortedImages.length > 1 && (
        <div className="product-gallery-thumbs">
          {sortedImages.map((image, index) => (
            <button
              key={image.id || index}
              onClick={() => setSelectedIndex(index)}
              className={`product-gallery-thumb ${index === selectedIndex ? 'product-gallery-thumb-active' : ''
                }`}
            >
              <img
                src={image.formats?.thumbnail?.url || image.url}
                alt={`Thumbnail ${index + 1}`}
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div
          className="product-gallery-overlay"
          onClick={() => setIsFullscreen(false)}
        >
          <div className="product-gallery-overlay-content" onClick={e => e.stopPropagation()}>
            <img
              src={selectedImage?.url}
              alt={`Product image ${selectedIndex + 1}`}
              className="product-gallery-overlay-img"
            />
            {sortedImages.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="product-gallery-nav product-gallery-nav-left"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  onClick={handleNext}
                  className="product-gallery-nav product-gallery-nav-right"
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}
          </div>
          <button
            onClick={() => setIsFullscreen(false)}
            className="product-gallery-close-btn"
            aria-label="Close fullscreen"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;